import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as faceapi from 'face-api.js';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import TopBar from '../components/TopBar';
import PageTransition from '../components/ui/PageTransition';
import WizardStepper from '../components/ui/WizardStepper';
import Button from '../components/ui/Button';
import Alert from '../components/ui/Alert';
import { AnimatePresence, motion } from 'framer-motion';
import './DetectionFace.css';
import { AxiosError } from 'axios';

type FaceStatus =
  | 'no-face'
  | 'too-far'
  | 'too-close'
  | 'move-left'
  | 'move-right'
  | 'move-up'
  | 'move-down'
  | 'centered';

// Tolerância de centralização/tamanho do guia de enquadramento (fração do frame da câmera)
const POSITION_TOLERANCE = 0.12;
const MIN_FACE_RATIO = 0.22;
const MAX_FACE_RATIO = 0.62;

const FACE_HINT_TEXT: Record<FaceStatus, string> = {
  'no-face': 'Posicione seu rosto dentro da moldura',
  'too-far': 'Aproxime-se um pouco',
  'too-close': 'Afaste-se um pouco',
  'move-left': 'Mova o rosto para a esquerda',
  'move-right': 'Mova o rosto para a direita',
  'move-up': 'Suba um pouco o rosto',
  'move-down': 'Abaixe um pouco o rosto',
  centered: 'Rosto centralizado. Irei tirar a foto.',
};

const AUTO_CAPTURE_SECONDS = 3;

const INSTRUCTIONS_SPEECH =
  'Antes de começar: fique em um ambiente bem iluminado, centralize o rosto dentro da moldura, ' +
  'retire óculos escuros, boné ou máscara, e olhe diretamente para a câmera.';

const speak = (text: string) => {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = 'pt-BR';
  window.speechSynthesis.speak(utterance);
};

interface Document {
  id_docs: number;
  name: string;
  url: string;
  upload_date: string;
  id_user: number;
}

interface ApiErrorResponse {
  message?: string;
  error?: string;
}

const DetectionFace: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [existingFacePhoto, setExistingFacePhoto] = useState<Document | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [captureSuccess, setCaptureSuccess] = useState(false);
  const [modelsReady, setModelsReady] = useState(false);
  const [modelsUnavailable, setModelsUnavailable] = useState(false);
  const [faceStatus, setFaceStatus] = useState<FaceStatus>('no-face');
  const [ready, setReady] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [countdown, setCountdown] = useState<number | null>(null);
  const lastSpokenStatusRef = useRef<FaceStatus | null>(null);
  const instructionsSpokenRef = useRef(false);
  const handleCaptureRef = useRef<() => void>(() => {});
  const autoCaptureAttemptedRef = useRef(false);
  const { token, id_user } = useAuth();
  const navigate = useNavigate();

  // Lê as instruções em voz alta quando o painel aparece
  useEffect(() => {
    if (ready || !voiceEnabled || instructionsSpokenRef.current) return;
    instructionsSpokenRef.current = true;
    speak(INSTRUCTIONS_SPEECH);
  }, [ready, voiceEnabled]);

  // Guia por voz durante a detecção: fala só quando o status muda
  useEffect(() => {
    if (!ready || !modelsReady || modelsUnavailable || uploading || captureSuccess) return;
    if (!voiceEnabled) {
      lastSpokenStatusRef.current = faceStatus;
      return;
    }
    if (lastSpokenStatusRef.current === faceStatus) return;
    lastSpokenStatusRef.current = faceStatus;
    speak(FACE_HINT_TEXT[faceStatus]);
  }, [faceStatus, ready, modelsReady, modelsUnavailable, uploading, captureSuccess, voiceEnabled]);

  // Anuncia o sucesso da captura
  useEffect(() => {
    if (captureSuccess && voiceEnabled) {
      speak('Rosto capturado com sucesso.');
    }
  }, [captureSuccess, voiceEnabled]);

  // Para de falar ao sair da página
  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // Carrega o modelo de detecção facial (TinyFaceDetector, roda 100% no navegador)
  useEffect(() => {
    let cancelled = false;
    const MODEL_URL = `${(process.env.PUBLIC_URL || '').replace(/\/+$/, '')}/models`;
    const giveUp = setTimeout(() => {
      if (!cancelled) setModelsUnavailable(true);
    }, 8000);

    faceapi.nets.tinyFaceDetector
      .loadFromUri(MODEL_URL)
      .then(() => {
        if (cancelled) return;
        clearTimeout(giveUp);
        setModelsReady(true);
      })
      .catch((err) => {
        console.error('Não foi possível carregar a verificação facial em tempo real:', err);
        if (!cancelled) {
          clearTimeout(giveUp);
          setModelsUnavailable(true);
        }
      });

    return () => {
      cancelled = true;
      clearTimeout(giveUp);
    };
  }, []);

  // Loop de detecção: a cada 300ms, checa se o rosto está centralizado e a distância certa
  useEffect(() => {
    if (!ready || !modelsReady || uploading || captureSuccess) return;

    let active = true;
    const options = new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.5 });

    const tick = async () => {
      const video = videoRef.current;
      if (!video || video.readyState < 2 || video.videoWidth === 0) return;

      const detection = await faceapi.detectSingleFace(video, options);
      if (!active) return;

      if (!detection) {
        setFaceStatus('no-face');
        return;
      }

      const { box } = detection;
      const frameW = video.videoWidth;
      const frameH = video.videoHeight;

      const sizeRatio = box.width / frameW;
      if (sizeRatio < MIN_FACE_RATIO) {
        setFaceStatus('too-far');
        return;
      }
      if (sizeRatio > MAX_FACE_RATIO) {
        setFaceStatus('too-close');
        return;
      }

      const offsetX = box.x + box.width / 2 - frameW / 2;
      const offsetY = box.y + box.height / 2 - frameH / 2;
      const xTolerancePx = frameW * POSITION_TOLERANCE;
      const yTolerancePx = frameH * POSITION_TOLERANCE;
      const xExceeds = Math.abs(offsetX) > xTolerancePx;
      const yExceeds = Math.abs(offsetY) > yTolerancePx;

      if (xExceeds || yExceeds) {
        const xRatio = Math.abs(offsetX) / xTolerancePx;
        const yRatio = Math.abs(offsetY) / yTolerancePx;

        if (xExceeds && (!yExceeds || xRatio >= yRatio)) {
          // o preview é espelhado (scaleX(-1)) para parecer um espelho de verdade,
          // mas a detecção roda no frame cru (não espelhado) da câmera — por isso
          // o sinal é invertido aqui pra instrução bater com o que o usuário vê na tela
          setFaceStatus(offsetX > 0 ? 'move-right' : 'move-left');
        } else {
          setFaceStatus(offsetY > 0 ? 'move-up' : 'move-down');
        }
        return;
      }

      setFaceStatus('centered');
    };

    tick();
    const intervalId = setInterval(tick, 300);
    return () => {
      active = false;
      clearInterval(intervalId);
    };
  }, [ready, modelsReady, uploading, captureSuccess]);

  // Contagem de 3s pra capturar sozinho assim que o rosto fica centralizado.
  // Se o status mudar antes de terminar (usuário se mexeu), o cleanup cancela tudo.
  useEffect(() => {
    if (faceStatus !== 'centered') {
      autoCaptureAttemptedRef.current = false;
    }

    if (faceStatus !== 'centered' || !ready || !modelsReady || uploading || captureSuccess) {
      setCountdown(null);
      return;
    }

    // já tentou uma vez nesta centralização (ex.: servidor recusou) — não fica repetindo sozinho
    if (autoCaptureAttemptedRef.current) return;

    setCountdown(AUTO_CAPTURE_SECONDS);
    const tickId = setInterval(() => {
      setCountdown((c) => (c !== null && c > 1 ? c - 1 : c));
    }, 1000);
    const captureId = setTimeout(() => {
      autoCaptureAttemptedRef.current = true;
      handleCaptureRef.current();
    }, AUTO_CAPTURE_SECONDS * 1000);

    return () => {
      clearInterval(tickId);
      clearTimeout(captureId);
    };
  }, [faceStatus, ready, modelsReady, uploading, captureSuccess]);

  // Carrega documentos e imagem existente
  useEffect(() => {
    if (!token || !id_user) {
      setErrorMessage('Sessão expirada. Faça login novamente.');
      return;
    }

    const fetchDocuments = async () => {
      try {
        const response = await api.get(`/docs/user/${id_user}`);
        if (response.data.message === 'User documents retrieved') {
          const facePhoto = response.data.data.find((doc: Document) => doc.name === 'Foto Capturada - Rosto');
          if (facePhoto) {
            setExistingFacePhoto(facePhoto);
            const filename = facePhoto.url.split('/').pop() || '';
            if (filename) {
              const imageResponse = await api.get(`/images/${filename}`, {
                responseType: 'blob',
              });
              const blobUrl = URL.createObjectURL(imageResponse.data);
              setPreviewUrl(blobUrl);
            }
          }
        }
      } catch (err) {
        const error = err as AxiosError<ApiErrorResponse>;
        console.error('Erro ao buscar documentos:', error.response?.status, error.response?.data);
        setErrorMessage(error.response?.data?.message || 'Erro ao carregar documentos. Tente novamente.');
      }
    };

    fetchDocuments();
  }, [token, id_user, navigate]); // Removido previewUrl das dependências

  // Inicializa a webcam — só depois que o usuário confirma as instruções
  useEffect(() => {
    if (!ready) return;

    const getWebcam = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error('Erro ao acessar webcam:', err);
        setErrorMessage('Erro ao acessar a webcam. Verifique as permissões.');
      }
    };

    getWebcam();

    return () => {
      if (videoRef.current && videoRef.current.srcObject instanceof MediaStream) {
        videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
      }
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [ready, previewUrl]); // Mantém previewUrl aqui para limpar a URL do blob

  const handleCapture = async () => {
    if (!token || !id_user) {
      setErrorMessage('Sessão expirada. Faça login novamente.');
      navigate('/rj');
      return;
    }

    const video = videoRef.current;
    if (!video) {
      setErrorMessage('Vídeo não disponível. Tente novamente.');
      return;
    }

    setUploading(true);
    setErrorMessage(null);

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setErrorMessage('Erro ao criar o contexto do canvas.');
      setUploading(false);
      return;
    }

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const base64Image = canvas.toDataURL('image/jpeg');

    try {
      // 1. Verificar detecção de face
      const detectRes = await fetch('https://prevcenso.com/flask-api/api/receber_dados_face', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ image: base64Image }),
      });

      if (!detectRes.ok) {
        const errorData = await detectRes.json();
        throw new Error(errorData.mensagem || 'Erro na detecção de face');
      }

      const detectData = await detectRes.json();
      if (detectData.status !== 'sucesso' || detectData.quantidade_faces === 0) {
        setErrorMessage('Nenhuma face detectada. Tente novamente.');
        setUploading(false);
        return;
      }

      // 2. Converter base64 para Blob
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob((b) => resolve(b), 'image/jpeg')
      );

      if (!blob) {
        setErrorMessage('Erro ao converter imagem.');
        setUploading(false);
        return;
      }

      // 3. Enviar imagem para upload
      const formData = new FormData();
      formData.append('image', blob, 'captura.jpg');
      const uploadRes = await api.post('/images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const fullUrl = uploadRes.data.url;
      if (!fullUrl) {
        throw new Error('Resposta inválida do upload.');
      }

      // 4. Salvar ou atualizar no banco
      const uploadDate = new Date().toISOString().split('T')[0];
      const payload = {
        name: 'Foto Capturada - Rosto',
        url: fullUrl,
        upload_date: uploadDate,
        id_user,
      };

      const url = existingFacePhoto ? `/docs/${existingFacePhoto.id_docs}` : '/docs';
      const method = existingFacePhoto ? 'put' : 'post';

      await api({ method, url, data: payload });

      // 5. Atualizar a lista de documentos
      const updated = await api.get(`/docs/user/${id_user}`);
      const newFacePhoto = updated.data.data.find((doc: Document) => doc.name === 'Foto Capturada - Rosto');
      setExistingFacePhoto(newFacePhoto || null);

      // 6. Carregar nova imagem para preview
      if (newFacePhoto) {
        const filename = newFacePhoto.url.split('/').pop() || '';
        if (filename) {
          const imageResponse = await api.get(`/images/${filename}`, {
            responseType: 'blob',
          });
          // Revogar a URL anterior, se existir
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
          }
          const blobUrl = URL.createObjectURL(imageResponse.data);
          setPreviewUrl(blobUrl);
        }
      }

      setCaptureSuccess(true);
      setTimeout(() => navigate('/rj/protocols'), 900);
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.error('Erro no fluxo de captura:', error.response?.status, error.response?.data);
      const message = error.response?.data?.message || error.response?.data?.error || (err instanceof Error ? err.message : 'Erro ao processar a imagem.');
      setErrorMessage(message);
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    handleCaptureRef.current = handleCapture;
  });

  const captureBlocked = modelsReady && faceStatus !== 'centered';

  const faceHint = modelsUnavailable
    ? null
    : !modelsReady
    ? 'Carregando verificação facial…'
    : FACE_HINT_TEXT[faceStatus];

  const guideClass =
    !modelsReady || modelsUnavailable ? '' : faceStatus === 'centered' ? 'face-frame--ready' : 'face-frame--warn';

  if (!token || !id_user) {
    return (
      <>
        <TopBar />
        <PageTransition>
          <div className="face-wrapper">
            <div className="face-container">
              <h2>Sessão expirada</h2>
              <p>Faça login novamente.</p>
              <Button onClick={() => navigate('/rj')} aria-label="Ir para a página de login">
                Ir para Login
              </Button>
            </div>
          </div>
        </PageTransition>
      </>
    );
  }

  return (
    <>
      <TopBar />
      <PageTransition>
        <div className="face-wrapper">
          <WizardStepper current={6} />
          <div className="face-container">
            <div className="face-header">
              <h2>Verificação Facial</h2>
              <button
                type="button"
                className={`face-voice-toggle${voiceEnabled ? '' : ' face-voice-toggle--muted'}`}
                onClick={() => setVoiceEnabled((v) => !v)}
                aria-pressed={voiceEnabled}
                aria-label={voiceEnabled ? 'Desativar orientação por voz' : 'Ativar orientação por voz'}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 9v6h4l5 5V4L8 9H4z" fill="currentColor" stroke="none" />
                  {voiceEnabled ? (
                    <>
                      <path d="M16.2 8.8a5 5 0 0 1 0 6.4" />
                      <path d="M18.8 6.2a8.5 8.5 0 0 1 0 11.6" />
                    </>
                  ) : (
                    <path d="M16.5 8.5l5 7M21.5 8.5l-5 7" />
                  )}
                </svg>
                {voiceEnabled ? 'Voz ativada' : 'Voz desativada'}
              </button>
            </div>
            <AnimatePresence>
              {errorMessage && <Alert variant="error">{errorMessage}</Alert>}
            </AnimatePresence>
            {existingFacePhoto && previewUrl && !captureSuccess ? (
              <div className="face-preview">
                <h3>Foto Capturada Anteriormente</h3>
                <img src={previewUrl} alt="Foto capturada" className="face-preview__image" />
              </div>
            ) : null}

            {ready ? (
              <>
                <div className={`face-frame${uploading ? ' face-frame--busy' : ''}${guideClass ? ` ${guideClass}` : ''}`}>
                  <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    className="face-video"
                    aria-label="Visualização da webcam para captura de rosto"
                  />

                  <span className="face-corner face-corner--tl" aria-hidden="true" />
                  <span className="face-corner face-corner--tr" aria-hidden="true" />
                  <span className="face-corner face-corner--bl" aria-hidden="true" />
                  <span className="face-corner face-corner--br" aria-hidden="true" />

                  <AnimatePresence>
                    {countdown !== null && !captureSuccess && (
                      <motion.div
                        key={countdown}
                        className="face-countdown-wrap"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15 }}
                      >
                        <motion.span
                          className="face-countdown"
                          initial={{ scale: 0.5, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ duration: 0.25, ease: 'easeOut' }}
                        >
                          {countdown}
                        </motion.span>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <AnimatePresence>
                    {captureSuccess && (
                      <motion.div
                        className="face-success"
                        initial={{ opacity: 0, scale: 0.7 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeOut' }}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 12l5 5L20 6" />
                        </svg>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <AnimatePresence mode="wait">
                    {!captureSuccess && faceHint && (
                      <motion.div
                        key={faceHint}
                        className={`face-guide-banner${faceStatus === 'centered' ? ' face-guide-banner--ready' : ''}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.2 }}
                        aria-live="polite"
                      >
                        <span className="face-guide-dot" aria-hidden="true" />
                        {faceHint}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <Button
                  onClick={handleCapture}
                  loading={uploading}
                  disabled={uploading || captureSuccess || captureBlocked}
                  aria-label={existingFacePhoto ? 'Atualizar captura de rosto' : 'Capturar e enviar foto de rosto'}
                >
                  {uploading ? 'Processando…' : existingFacePhoto ? 'Atualizar Captura' : 'Capturar & Enviar'}
                </Button>
              </>
            ) : (
              <div className="face-instructions">
                <h3>Antes de começar</h3>
                <ul className="face-instructions__list">
                  <li>
                    <span className="face-instructions__dot" aria-hidden="true" />
                    Fique em um ambiente bem iluminado
                  </li>
                  <li>
                    <span className="face-instructions__dot" aria-hidden="true" />
                    Centralize o rosto dentro da moldura
                  </li>
                  <li>
                    <span className="face-instructions__dot" aria-hidden="true" />
                    Retire óculos escuros, boné ou máscara
                  </li>
                  <li>
                    <span className="face-instructions__dot" aria-hidden="true" />
                    Olhe diretamente para a câmera
                  </li>
                </ul>
                <Button onClick={() => setReady(true)} aria-label="Confirmar instruções e iniciar a verificação facial">
                  Estou pronto, iniciar
                </Button>
              </div>
            )}
          </div>
        </div>
      </PageTransition>
    </>
  );
};

export default DetectionFace;